/* eslint-disable react/prop-types */
import React, { MouseEvent, ReactNode, useCallback } from "react"
import cx from "clsx"
import { Link } from "gatsby"
import { useMenuContext } from "context/menu"
import { noop } from "lib/helpers"
import { MenuItem } from "lib/menuItems"
import { track } from "lib/segment"
import SidebarItemLabel from "./SidebarItemLabel"

export interface SidebarItemProps extends MenuItem {
  menuCollapsed?: boolean
  setMenuCollapsed?: (menuCollapsed: boolean) => void
}

const SidebarItem = ({
  id,
  url,
  label,
  items = [],
  tier = 0,
  menuCollapsed = true,
  setMenuCollapsed = noop,
  isNew,
  external
}: SidebarItemProps) => {
  const { menuState, toggleMenuItem } = useMenuContext()
  const isItemCollapsed = !menuState.includes(id)
  const contentId = `${id}_content`

  const handleClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      // Re-expand the sidebar if a top level item gets expanded
      if (tier === 0 && menuCollapsed) {
        setMenuCollapsed(false)
        // Avoid opening the sidebar but closing the menu item
        if (isItemCollapsed) {
          toggleMenuItem(id)
        }
      } else {
        toggleMenuItem(id)
      }
    },
    [isItemCollapsed, id, menuCollapsed, setMenuCollapsed, tier, toggleMenuItem]
  )

  const trackMenuItemClick = useCallback(() => {
    if (id === `analytics/product/store-sales`) {
      track(`Store Sales Clicked`)
    }
  }, [id])

  // Wrap the url in a Link or button depending on if a url is provided
  const Wrapper = useCallback(
    ({ children }: { children: ReactNode }): JSX.Element => {
      const commonProps = {
        id: `sidebar-item-${id}`,
        "data-testid": id,
        "data-tier": tier.toString(),
        className: cx(
          `group/link relative inline-flex w-full items-center text-left aria-[current=page]:w-[calc(100%-1rem)]`,
          { "font-medium capitalize": tier === 0 }
        )
      }

      const handleItemClick = (e: MouseEvent) => {
        trackMenuItemClick()
        if (items?.length) {
          handleClick(e)
        }
      }

      const accessibilityProps = items?.length
        ? {
            "aria-controls": contentId,
            "aria-expanded": !isItemCollapsed,
            onClick: handleItemClick
          }
        : { onClick: trackMenuItemClick }

      if (url && url.startsWith(`/`)) {
        return (
          <Link to={url} {...commonProps} {...accessibilityProps}>
            {children}
          </Link>
        )
      }

      if (url) {
        return (
          <a
            href={url}
            {...commonProps}
            {...accessibilityProps}
            {...(external && { target: `_blank`, rel: `noreferrer` })}
          >
            {children}
          </a>
        )
      }

      return (
        <button type="button" {...commonProps} {...accessibilityProps}>
          {children}
        </button>
      )
    },
    [
      id,
      tier,
      items?.length,
      contentId,
      isItemCollapsed,
      handleClick,
      trackMenuItemClick,
      url,
      external
    ]
  )

  return (
    <li className="mb-2 w-full last:mb-0">
      <Wrapper>
        <SidebarItemLabel
          id={id}
          label={label}
          hasItems={Boolean(items?.length)}
          menuCollapsed={menuCollapsed}
          isNew={isNew}
        />
      </Wrapper>

      {items?.length ? (
        <ul
          id={contentId}
          data-testid={contentId}
          className="pl-8 mt-2 group-data-[collapsed=true]/sidebar:hidden [[aria-expanded=false]+&]:hidden"
        >
          {items?.map(item => (
            // Render child items and increment tier
            <SidebarItem
              key={item.id}
              tier={tier + 1}
              {...item}
              menuCollapsed={menuCollapsed}
              setMenuCollapsed={setMenuCollapsed}
            />
          ))}
        </ul>
      ) : null}
    </li>
  )
}

export default SidebarItem
